import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { fetchOperatorContext, fetchStock, findOrder, lookupCode, stockTargetKey } from './operatorThunks';
import { resolveOperatorWarehouse } from './operatorUtils';
import { HOME_TAB, LOOKUP_HISTORY_SIZE } from './operatorConstants';
import { acceptedCode, cleanCode } from './operatorScanGate';

const HOME_TABS = Object.values(HOME_TAB);

/*
 * The warehouse operator's workspace: who they are, which warehouses they
 * reach and what each one lets them do, as `GET operators/me/` says it.
 *
 * The warehouse is chosen once, on Inicio, and every flow works in it.
 */
const initialState = {
  profile: null,
  /** `assigned` | `admin_fallback` | `none` */
  source: null,
  permissions: [],
  /** What this backend can do beyond the base, as `me` says it: `picking_route`… */
  capabilities: [],
  warehouses: [],
  loading: false,
  loaded: false,
  error: null,
  requestId: null,
  selectedWarehouseId: null,
  /** This device's choices; `homeTab` is the tab Inicio opens on, `cameraDiagnostics` shows what the camera sees. */
  prefs: { sound: true, vibration: true, camera: true, cameraDiagnostics: false, homeTab: HOME_TAB.TASKS },
  /**
   * Consulta, and the stock of one of its results (`target` is `{kind, id}`).
   * Only the last request of each shows its answer (`requestId`).
   */
  lookup: {
    loading: false,
    lastCode: null,
    code: null,
    results: [],
    error: null,
    requestId: null,
    history: [],
    stock: { target: null, loading: false, error: null, data: null, requestId: null },
  },
  /**
   * An order scanned at the picking or packing queue (`flow`): the last code
   * that queue accepted, and the order found when it goes somewhere else.
   */
  orderScan: { flow: null, finding: false, lastCode: null, result: null, requestId: null },
};

const operatorSlice = createSlice({
  name: 'operator',
  initialState,
  reducers: {
    operatorWarehouseSelected: (state, action) => {
      state.selectedWarehouseId = action.payload;
      state.lookup = initialState.lookup;
      state.orderScan = initialState.orderScan;
    },
    /** A queue opened: the order it found last time may have moved since. Its last code stays. */
    orderScanEntered: (state) => {
      state.orderScan.result = null;
    },
    /** @param {{payload: {pref: 'sound'|'vibration'|'camera'|'cameraDiagnostics', value: boolean}}} action */
    operatorPrefChanged: (state, action) => {
      state.prefs = { ...state.prefs, [action.payload.pref]: action.payload.value };
    },
    /** @param {{payload: string}} action - A tab of `HOME_TAB`; any other is ignored. */
    operatorHomeTabChosen: (state, action) => {
      if (!HOME_TABS.includes(action.payload) || state.prefs?.homeTab === action.payload) return;
      state.prefs = { ...state.prefs, homeTab: action.payload };
    },
    lookupCleared: (state) => {
      state.lookup = { ...initialState.lookup, history: state.lookup.history };
    },
    /** Consulta opened: the figures it showed may have moved since. What each code is has not. */
    lookupEntered: (state) => {
      state.lookup.stock = initialState.lookup.stock;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOperatorContext.pending, (state, action) => {
        state.loading = true;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchOperatorContext.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        const { operator, warehouses_source, wms_permissions, capabilities, warehouses } = action.payload;
        state.loading = false;
        state.loaded = true;
        state.error = null;
        state.profile = operator || null;
        state.source = warehouses_source || null;
        state.permissions = wms_permissions || [];
        state.capabilities = capabilities || [];
        state.warehouses = warehouses || [];
        state.selectedWarehouseId = resolveOperatorWarehouse(state.warehouses, state.selectedWarehouseId);
      })
      .addCase(fetchOperatorContext.rejected, (state, action) => {
        if (action.meta.condition || action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload ?? { kind: 'refused', message: action.error.message };
      })

      // Limpiar and another warehouse drop the scan on its way, as they do its stock.
      .addCase(lookupCode.pending, (state, action) => {
        const code = cleanCode(action.meta.arg.code);
        state.lookup.loading = true;
        state.lookup.code = code;
        state.lookup.error = null;
        state.lookup.requestId = action.meta.requestId;
        state.lookup.stock = initialState.lookup.stock;
      })
      .addCase(lookupCode.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.lookup.requestId) return;
        const code = cleanCode(action.meta.arg.code);
        state.lookup.lastCode = acceptedCode(action);
        state.lookup.loading = false;
        state.lookup.results = action.payload.results || [];
        state.lookup.history = [code, ...state.lookup.history.filter((item) => item !== code)].slice(
          0,
          LOOKUP_HISTORY_SIZE
        );
      })
      .addCase(lookupCode.rejected, (state, action) => {
        if (action.meta.condition || action.meta.requestId !== state.lookup.requestId) return;
        state.lookup.loading = false;
        state.lookup.results = [];
        state.lookup.error = action.payload ?? { kind: 'refused', message: action.error.message };
      })

      // A scan at the other queue replaces one still out.
      .addCase(findOrder.pending, (state, action) => {
        const { flow } = action.meta.arg;
        state.orderScan = {
          flow,
          finding: true,
          lastCode: state.orderScan.flow === flow ? state.orderScan.lastCode : null,
          result: null,
          requestId: action.meta.requestId,
        };
      })
      .addCase(findOrder.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.orderScan.requestId) return;
        state.orderScan.finding = false;
        state.orderScan.lastCode = acceptedCode(action);
        state.orderScan.result = action.payload;
      })
      .addCase(findOrder.rejected, (state, action) => {
        if (action.meta.condition || action.meta.requestId !== state.orderScan.requestId) return;
        state.orderScan.finding = false;
      })

      // Only the last request's answer shows: a new scan, Limpiar, another result or opening Consulta
      // again drop the one on its way.
      .addCase(fetchStock.pending, (state, action) => {
        const { kind, id } = action.meta.arg;
        state.lookup.stock = {
          target: { kind, id },
          loading: true,
          error: null,
          data: null,
          requestId: action.meta.requestId,
        };
      })
      .addCase(fetchStock.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.lookup.stock.requestId) return;
        state.lookup.stock.loading = false;
        state.lookup.stock.data = action.payload;
      })
      .addCase(fetchStock.rejected, (state, action) => {
        if (action.meta.condition || action.meta.requestId !== state.lookup.stock.requestId) return;
        state.lookup.stock.loading = false;
        state.lookup.stock.error = action.payload ?? { kind: 'refused', message: action.error.message };
      });
  },
});

export const {
  operatorWarehouseSelected,
  operatorPrefChanged,
  operatorHomeTabChosen,
  lookupCleared,
  lookupEntered,
  orderScanEntered,
} = operatorSlice.actions;

/**
 * The order the queue of that flow found and does not open: it goes
 * somewhere else. `null` otherwise.
 *
 * @param {string} flow
 */
export const selectOrderElsewhere = (flow) => (state) => {
  const { orderScan } = state.operator;
  return orderScan.flow === flow && orderScan.result && orderScan.result.next_flow !== flow ? orderScan.result : null;
};

const stockKeyOf = ({ target }) => (target ? stockTargetKey(target) : null);

/** The Consulta result whose stock is on screen, as `stockTargetKey` names it, or `null`. */
export const selectShownStockKey = (state) => stockKeyOf(state.operator.lookup.stock);

/**
 * The result whose stock is loading, or `null`. Only the request the screen
 * still waits for counts: one that a new scan, Limpiar or another result
 * replaced stays in flight until it answers or times out (120 s).
 */
export const selectLoadingStockKey = (state) => {
  const { stock } = state.operator.lookup;
  return stock.loading ? stockKeyOf(stock) : null;
};

// The warehouse and the device's choices survive a reload; the rest is the
// server's call and is asked again.
const persistConfig = {
  key: 'operator',
  storage,
  whitelist: ['selectedWarehouseId', 'prefs'],
};

export default persistReducer(persistConfig, operatorSlice.reducer);
